import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Globe, ArrowRight, Loader2 } from "lucide-react";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
    { icon: Mail, label: "Email", value: "info@gpsrls.com", href: "mailto:info@gpsrls.com", testid: "contact-info-email" },
    { icon: Phone, label: "Telefono", value: "392 646 4619", href: "tel:+393926464619", testid: "contact-info-phone" },
    { icon: MapPin, label: "Sede", value: "Mirano (Venezia), Italia", testid: "contact-info-address" },
    { icon: Globe, label: "Area", value: "Tutta Italia", testid: "contact-info-area" },
];

const inputClass =
    "w-full border-b border-[#1A1A1A]/15 bg-transparent py-3 text-base text-[#1A1A1A] placeholder-[#1A1A1A]/30 outline-none transition-colors duration-300 focus:border-[#B88282]";

export default function Contatti() {
    const [form, setForm] = useState({ nome: "", email: "", messaggio: "" });
    const [sending, setSending] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("Messaggio inviato. Ti risponderemo al più presto.");
            setForm({ nome: "", email: "", messaggio: "" });
        } catch {
            toast.error("Qualcosa non ha funzionato. Riprova o scrivici a info@gpsrls.com.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contatti" data-testid="contatti-section" className="bg-[#FAF7F2] py-24 sm:py-32">
            <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-2">
                <div>
                    <Reveal>
                        <p className="text-xs font-medium uppercase tracking-[0.3em] text-[#B88282]">Contatti</p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-4 font-serif text-4xl leading-tight tracking-tight sm:text-5xl">
                            Parliamo del tuo <span className="italic text-[#B88282]">obiettivo</span>.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-6 max-w-md text-base leading-relaxed text-[#5A5A5A] sm:text-lg">
                            Raccontaci la tua sfida: ti risponderemo con ascolto e una proposta costruita sulla tua
                            realtà.
                        </p>
                    </Reveal>

                    <div className="mt-12 space-y-6">
                        {INFO.map((item, i) => (
                            <Reveal key={item.testid} delay={0.08 * i}>
                                <div data-testid={item.testid} className="flex items-center gap-5">
                                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#D4A5A5]/15 text-[#B88282]">
                                        <item.icon className="h-5 w-5" strokeWidth={1.6} />
                                    </div>
                                    <div>
                                        <p className="text-xs uppercase tracking-[0.25em] text-[#5A5A5A]">
                                            {item.label}
                                        </p>
                                        {item.href ? (
                                            <a
                                                href={item.href}
                                                className="text-lg font-medium text-[#1A1A1A] transition-colors hover:text-[#B88282]"
                                            >
                                                {item.value}
                                            </a>
                                        ) : (
                                            <p className="text-lg font-medium text-[#1A1A1A]">{item.value}</p>
                                        )}
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>

                <Reveal delay={0.15}>
                    <form
                        data-testid="contact-form"
                        onSubmit={submit}
                        className="rounded-2xl border border-[#1A1A1A]/8 bg-white p-8 shadow-[0_32px_80px_-40px_rgba(26,26,26,0.2)] sm:p-10"
                    >
                        <div className="space-y-8">
                            <div>
                                <label
                                    htmlFor="contact-nome"
                                    className="text-xs uppercase tracking-[0.25em] text-[#5A5A5A]"
                                >
                                    Nome e cognome
                                </label>
                                <input
                                    id="contact-nome"
                                    data-testid="contact-input-nome"
                                    type="text"
                                    required
                                    minLength={2}
                                    value={form.nome}
                                    onChange={set("nome")}
                                    placeholder="Il tuo nome"
                                    className={inputClass}
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="contact-email"
                                    className="text-xs uppercase tracking-[0.25em] text-[#5A5A5A]"
                                >
                                    Email
                                </label>
                                <input
                                    id="contact-email"
                                    data-testid="contact-input-email"
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={set("email")}
                                    placeholder="nome@azienda.it"
                                    className={inputClass}
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="contact-messaggio"
                                    className="text-xs uppercase tracking-[0.25em] text-[#5A5A5A]"
                                >
                                    Messaggio
                                </label>
                                <textarea
                                    id="contact-messaggio"
                                    data-testid="contact-input-messaggio"
                                    required
                                    minLength={5}
                                    rows={4}
                                    value={form.messaggio}
                                    onChange={set("messaggio")}
                                    placeholder="Raccontaci il tuo obiettivo..."
                                    className={`${inputClass} resize-none`}
                                />
                            </div>
                        </div>
                        <button
                            data-testid="contact-submit-button"
                            type="submit"
                            disabled={sending}
                            className="group mt-10 flex w-full items-center justify-center gap-2 rounded-full bg-[#1A1A1A] py-4 text-sm font-medium tracking-wide text-white transition-colors duration-300 hover:bg-[#D4A5A5] hover:text-[#1A1A1A] disabled:opacity-60"
                        >
                            {sending ? (
                                <>
                                    Invio in corso
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                </>
                            ) : (
                                <>
                                    Invia
                                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                </>
                            )}
                        </button>
                    </form>
                </Reveal>
            </div>
        </section>
    );
}
